import { Alert, Box, ButtonProps, Grid, IconProps, TextContent } from '@amzn/awsui-components-core';
import {
  Autotag,
  ContentWorkflow,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import ContentMetadataForm from './ContentMetadataForm';
import * as UtilApi from '../../api/UtilsApi';
import { validateTemplateMetadata } from '../../api/ValidationUtil';
import { ContentSchemaSection } from '../../model/ContentSchemaSection';
import { metadataToAutotagTypeMap } from '../utils/CreateEditContentUtils';
import constants from '../../common/constants';
import { AppContext } from '../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';

interface Props {
  buttonText?: string;
  iconAlign?: ButtonProps.IconAlign;
  iconName?: IconProps.Name;
  readOnly?: boolean;
  variant?: 'normal' | 'primary' | 'icon' | 'text' | 'link';
  workflow: ContentWorkflow;
}

const EditMetadataButton = (props: Props) => {
  const {
    contentId,
    draftContentMetadata,
    draftContentTypeMetadata,
    draftMetadataTags,
    publicationMetadataTags,
  } = props.workflow;
  const { contentName, contentType } = draftContentMetadata;
  const templateSchema = UtilApi.getContentTemplateSchema(
    contentType,
    draftContentTypeMetadata,
    ContentSchemaSection.TemplateMetadata,
  );

  const appContext = useContext(AppContext);
  const entities = appContext?.entities || {};

  // @ts-expect-error TS2345 - strict migration
  const [readOnly, setReadOnly] = useState<boolean>(props.readOnly);
  const [isPublication, setIsPublication] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [templateMetadata, setTemplateMetadata] = useState<any>();
  const [systemAddedTags, setSystemAddedTags] = useState<Autotag[]>();
  const [inputErrors, setInputErrors] = useState<Map<string, string>>(new Map());
  const mplServiceClient: IMplServiceClient = useMemo(() => MplServiceClientFactory.create(), []);

  useEffect(() => {
    initializeMetadata(props.workflow);
  }, [props.workflow]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLatestWorkflow = async () => {
    setIsUpdating(true);
    try {
      const response = await mplServiceClient.getContentWorkflow(contentId);
      initializeMetadata(response.workflow);
    } finally {
      setIsUpdating(false);
    }
    return true;
  };

  const initializeMetadata = (workflow: ContentWorkflow) => {
    const {
      draftState,
      draftTemplateMetadata,
      draftMetadataTags: draftTags,
      publicationTemplateMetadata,
      publicationMetadataTags: publicationTags,
      publicationState,
    } = workflow;
    setInputErrors(new Map());
    if (!!draftTags?.systemAddedTags?.length) {
      setSystemAddedTags(draftTags?.systemAddedTags || []);
      setTemplateMetadata(draftTemplateMetadata);
      setReadOnly(
        props.readOnly || (!!draftState && [WorkflowState.REVIEWED].includes(draftState)),
      );
      setIsPublication(false);
    } else if (!!publicationTags?.systemAddedTags?.length) {
      setSystemAddedTags(publicationTags?.systemAddedTags || []);
      setTemplateMetadata(publicationTemplateMetadata);
      setReadOnly(
        props.readOnly ||
          (!!publicationState && ![WorkflowState.PUBLISHED].includes(publicationState)),
      );
      setIsPublication(true);
    } else {
      setReadOnly(true);
    }
  };

  const handleTemplateMetadataUpdate = (sectionId: string, updatedValue: any) => {
    const updatedTemplateMetadata = templateMetadata;
    updatedTemplateMetadata[sectionId] = updatedValue;
    let updatedSystemAddedTags = systemAddedTags;
    const autotagType = metadataToAutotagTypeMap.get(sectionId);
    if (autotagType) {
      const unmodifiedTags =
        systemAddedTags?.filter((systemAddedTag) => systemAddedTag.type !== autotagType) || [];
      const modifiedTag = systemAddedTags?.find(
        (systemAddedTag) => systemAddedTag.type === autotagType,
      );
      if (modifiedTag) {
        modifiedTag.tags = modifiedTag?.tags?.filter((sectionTag) =>
          updatedValue.includes(sectionTag),
        );
        updatedSystemAddedTags = [...unmodifiedTags, modifiedTag];
      }
    }
    const errorMap = validateTemplateMetadata(templateSchema, updatedTemplateMetadata);
    setInputErrors(errorMap);
    setTemplateMetadata(updatedTemplateMetadata);
    setSystemAddedTags(updatedSystemAddedTags);
  };

  const handleSubmit = async () => {
    setIsUpdating(true);
    try {
      if (isPublication) {
        await mplServiceClient.updatePublicationMetadata(contentId, templateMetadata);
      } else {
        await mplServiceClient.updateDraftMetadata(contentId, templateMetadata);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // @ts-expect-error TS7030 - strict migration
  const renderError = () => {
    if (inputErrors.size > 0) {
      return (
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <Alert type="error">
            <TextContent>
              <ul>
                {[...inputErrors.entries()].map((e) => (
                  <li key={e[0]}>
                    <Box variant="strong">{e[0]}:</Box> {e[1]}
                  </li>
                ))}
              </ul>
            </TextContent>
          </Alert>
        </Grid>
      );
    }
  };

  const metadataForm = (): JSX.Element => {
    return (
      <div className="awsui">
        {renderError()}
        <div id="metadata">
          <Grid gridDefinition={[{ colspan: 11 }]}>
            <ContentMetadataForm
              onTemplateMetadataUpdated={handleTemplateMetadataUpdate}
              readonly={readOnly}
              showAutotagTemplate
              autotags={systemAddedTags}
              templateSchema={templateSchema}
              templateData={templateMetadata}
              visible
            />
          </Grid>
        </div>
      </div>
    );
  };

  if (
    !draftMetadataTags?.systemAddedTags?.length &&
    !publicationMetadataTags?.systemAddedTags?.length
  ) {
    return null;
  }

  return (
    <ModalDialogButtonV3
      header={`Edit details for ${isPublication ? 'publication' : 'draft'} - ${convertEntities(contentName, entities)}`}
      onBeforeOpenModal={fetchLatestWorkflow}
      inputForm={metadataForm}
      actionControls={!readOnly}
      inputValid={inputErrors.size === 0}
      onSubmit={handleSubmit}
      variant={props.variant}
      iconAlign={props.iconAlign}
      iconName={props.iconName}
      buttonText={props.buttonText}
      size={'large'}
      submitButtonText={constants.CONFIRM_BUTTON_TEXT}
      visible
      loading={isUpdating}
    />
  );
};

export default EditMetadataButton;
